import React, { useEffect, useState } from 'react';
import { Drawer, Skeleton } from 'antd';
import ApplicantHeader from '@components/app-components/applicant/applicant-header/ApplicantHeader';
import { ApplicantElement, SmallApplicantElement, StepApplicantElement } from '@models/applicants';
import ArrowLeftIcon from '@assets/svgs/left-grey-arrow.svg';
import ArrowRightIcon from '@assets/svgs/right-grey-arrow.svg';
import WhitePin from '@assets/svgs/white-pin.svg';
import { getApplicant } from '@services/applicants/applicants-http.service';
import CvCard from '@components/app-components/applicant/cv-card/CvCard';
import ApplicantComments from '@components/app-components/applicant/applicant-comments/ApplicantComments';
import useNotification from '@hooks/notification/useNotification';
import StateEnum from '@constants/state.enum';
import ApplicantDetails from '@components/app-components/applicant/applicant-details/ApplicantDetails';
import ApplicantTags from '@components/app-components/applicant/applicant-tags/ApplicantTags';
import UiButton from '@components/ui/button/UiButton';
import ApplicantCorrelation from '@components/app-components/applicant/applicant-correlation/ApplicantCorrelation';
import ApplicantConnectionCard from '@components/app-components/applicant/applicant-connection-card/ApplicantConnectionCard';
import { useTranslation } from 'next-i18next';
import styles from './Applicant.module.scss';

export type ApplicantType = Array<SmallApplicantElement> | Array<StepApplicantElement>;
type ApplicantProps = {
  applicantId: number;
  applicants: ApplicantType;
  setVisible: (value: boolean) => void;
  visible: boolean;
  fetchApplicants?: () => void;
  arrows?: boolean;
};

const Applicant: React.FC<ApplicantProps> = ({
  applicants,
  setVisible,
  visible,
  applicantId,
  fetchApplicants,
  arrows,
}) => {
  const { t: translate, i18n } = useTranslation('applicant');
  const [applicantIdState, setApplicantIdState] = useState<number>(applicantId);
  const [applicant, setApplicant] = useState<ApplicantElement>();
  const [correlation, setCorrelation] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const fetchApplicant = async () => {
    setLoading(true);
    if (applicantIdState === -1) {
      await setApplicantIdState(applicantId);
      if (fetchApplicants) await fetchApplicants();
    }
    if (applicantIdState !== -1) {
      try {
        const applicantElem: ApplicantElement = await getApplicant(applicantIdState, i18n.language);
        await setApplicant(applicantElem);
        if (fetchApplicants) await fetchApplicants();
        await setLoading(false);
      } catch (e: any) {
        generalError();
        setLoading(false);
      }
    }
  };

  // eslint-disable-next-line consistent-return
  const applicantPositions = () => {
    if (applicant) {
      return applicant.positions?.map((position: any) => {
        return <ApplicantConnectionCard key={position.id} position={position} />;
      });
    }
  };

  useEffect(() => {
    fetchApplicant();
    setCorrelation(false);
  }, [applicantIdState, visible]);

  return (
    <Drawer
      key={applicantId}
      size="large"
      placement="right"
      onClose={() => {
        setApplicantIdState(-1);
        setVisible(false);
      }}
      open={visible}
      closable={false}
      width={912}
      rootClassName={styles.applicantDrawer}
    >
      {applicants.length > 1 && arrows ? (
        <div role="button" className={styles.wrapArrow}>
          <button
            className={styles.arrow}
            type="submit"
            onClick={async () => {
              const index = applicants.findIndex((obj) => obj.id === applicantIdState);
              if (index === 0) {
                await setApplicantIdState(applicants[applicants.length - 1].id);
              } else if (index) {
                await setApplicantIdState(applicants[index - 1].id);
              }
            }}
          >
            <ArrowLeftIcon />
          </button>
        </div>
      ) : (
        <div className={styles.emptyArrows} />
      )}
      {applicant && !loading ? (
        <div className={styles.wrapApplicantBody}>
          <div className={styles.wrapApplicantHeader}>
            <ApplicantHeader applicant={applicant} />
            <div className={styles.wrapCorrelationButton}>
              <UiButton
                size="small"
                type="primary"
                icon={<WhitePin />}
                onClick={() => setCorrelation(true)}
                disabled={correlation}
              >
                {translate('correlation')}
              </UiButton>
            </div>
          </div>
          <ApplicantTags applicantId={applicant.id} tags={applicant.tags} />
          {!correlation ? (
            <>
              <div className={styles.wrapApplicantConnectionsScroll}>
                <div className={styles.wrapApplicantConnections}>
                  <CvCard
                    cv={applicant.cvId}
                    createdDate={applicant.cvInsertedDate}
                    applicantId={applicant.id}
                    fetchApplicant={fetchApplicant}
                  />
                  {applicantPositions()}
                </div>
              </div>
              <ApplicantComments
                applicantId={applicant.id.toString()}
                fetchApplicants={() => {
                  if (fetchApplicants) fetchApplicants();
                }}
              />
            </>
          ) : (
            <ApplicantCorrelation
              applicant={applicant}
              setCorrelation={setCorrelation}
              fetchApplicant={() => fetchApplicant()}
            />
          )}
          <ApplicantDetails
            applicant={applicant}
            fetchApplicant={() => fetchApplicant()}
            fetchApplicants={() => {
              if (fetchApplicants) fetchApplicants();
            }}
          />
        </div>
      ) : (
        <Skeleton paragraph={{ rows: 6 }} active />
      )}
      {applicants.length > 1 && arrows ? (
        <div className={styles.wrapArrow}>
          <button
            className={styles.arrow}
            type="submit"
            onClick={async () => {
              const index = applicants.findIndex((obj) => obj.id === applicantIdState);
              if (index === applicants.length - 1) {
                await setApplicantIdState(applicants[0].id);
              } else if (index >= 0) {
                await setApplicantIdState(applicants[index + 1].id);
              }
            }}
          >
            <ArrowRightIcon />
          </button>
        </div>
      ) : (
        <div className={styles.emptyArrows} />
      )}
    </Drawer>
  );
};

Applicant.defaultProps = {
  fetchApplicants: undefined,
};

export default Applicant;
